import React, { useState } from "react";
import { useParams } from "react-router-dom";

import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";

// Types
import type { ResourceLogModelFrontend } from "@performyard/service-contracts";

import Button from "../../../components/Button";
import type { SingleEmployee } from "../../../components/EmployeePicker";
import EmployeeSearch from "../../../components/EmployeeSearch";
import FormattedDateTime from "../../../components/FormattedDateTime";
import ContentModal from "../../../components/Modal/Templates/ContentModal";
import Pagination from "../../../components/Pagination";
import Spinner from "../../../components/Spinner";
import TableLite from "../../../components/TableLite";
import type { Column } from "../../../components/TableLite/types";
import Text from "../../../components/Text";

import ErrorPage from "../../../pages/Error";

// Hooks
import { useRbacObjectPermissions, useReviewCycleLogs, useReviewCycleMeta, useUser } from "../../../api/hooks";

// Utils
import get from "../../../utils/GetNested";
import { getCriteriaCell, getModalContent, getSubjectCell } from "./utils";

// Components
import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
// CSS
import css from "./AssignmentLog.module.scss";

const AssignmentLog = (props: { pageSize: number }) => {
	const { pageSize } = props;

	const { cycleId: reviewCycleId } = useParams();

	if (!reviewCycleId) {
		console.error("Missing review-cycle ID");
		return null;
	}

	const { data: reviewCyclePermissions, isLoading: isReviewCyclePermissionsLoading } = useRbacObjectPermissions(
		"review",
		reviewCycleId
	);

	// Table content - width adds up to 1104px
	const columns: Column[] = [
		{
			id: "subject",
			label: "Subject",
			isSortable: true,
			width: "236px", // 236px + 4px grid gap + 16px left padding = 256px
			getter: getSubjectCell,
		},
		{
			id: "assignedDate",
			label: "Assigned Date",
			isSortable: true,
			width: "248px", // 248px + 4px grid gap + 4px grid gap = 256px
			getter: (resourceLog: ResourceLogModelFrontend) => (
				<div className={css.assignedDateBox}>
					<FormattedDateTime
						size="paragraph-3"
						date={get(resourceLog, ["changedAt"], null)}
						formatString="MMM d, yyyy h:mmaaa"
					/>
				</div>
			),
		},
		{
			id: "assignedCriteria",
			label: "Assigned Criteria",
			isSortable: false,
			width: "480px", // 480px + 8px grid gap = 488px
			getter: getCriteriaCell,
		},
		{
			id: "moreDetailsButton",
			label: "",
			isSortable: false,
			width: "88px", // 88px + 16px right padding = 104px
			getter: (resourceLog: ResourceLogModelFrontend) => {
				if (resourceLog.changeType.includes("automated")) {
					const subjectId: string | null = get(resourceLog, ["data", "subjectId"], null);
					if (!subjectId) return null;
					const { data: userData } = useUser({ userID: subjectId });

					return (
						<div className={css.moreDetailsButtonContainer}>
							<Button
								className={css.moreDetailsButton}
								size={"small"}
								onClick={() => setModalContent({ resourceLog, subjectName: userData?.name || "" })}
							>
								<Text size="caption" weight="regular">
									More Details
								</Text>
							</Button>
						</div>
					);
				}
			},
		},
	];

	// Paging
	const [currentPage, setCurrentPage] = useState(1);
	const handleChangePage = (page: number) => {
		setCurrentPage(page);
	};

	// Sorting
	const [currentSort, setCurrentSort] = useState<{ name: string; direction: "asc" | "desc" }>({
		name: "assignedDate",
		direction: "desc",
	});
	const handleSortColumn = (columnId: string, direction: "asc" | "desc") => {
		setCurrentSort({ name: columnId, direction });
	};

	// Searching subject
	const [searchSubject, setSearchSubject] = useState<SingleEmployee | null>(null);
	const handleEmployeeSearch = (employee: SingleEmployee) => {
		setSearchSubject(employee);
	};

	// Details Modal state
	const [modalContent, setModalContent] = useState<{
		resourceLog: ResourceLogModelFrontend;
		subjectName: string;
	} | null>(null);

	// Resource logs
	const {
		reviewCycleLogs,
		totalCount: totalReviewCycleLogs,
		isLoading: isReviewCycleLogsLoading,
	} = useReviewCycleLogs({
		resourceTypes: ["review-cycle"],
		resourceIds: [reviewCycleId],
		changeTypes: ["automated-add-subject", "add-subject", "remove-subject"],
		subjectIds: searchSubject ? [searchSubject.value] : null,
		limit: pageSize,
		skip: (currentPage - 1) * pageSize,
		sort: currentSort,
	});

	// Review Cycle meta data
	const { data: reviewCycle, isLoading: isReviewCycleLoading } = useReviewCycleMeta(reviewCycleId);
	const pageHeader = reviewCycle?.name || "Review Cycle";
	const linkToReviewCycle = `/review-cycle/${reviewCycleId}`;

	const mainContent = (
		<div className={css.mainContent}>
			<div className={css.navigationHeader}>
				<Button
					isHollow
					className={css.backToReviewCycleButton}
					size="medium"
					icon={faArrowLeft}
					href={linkToReviewCycle}
				>
					<Text size="paragraph-3" weight="semi-bold">
						Back to Review Cycle
					</Text>
				</Button>
			</div>

			<div className={css.headersContainer}>
				<div>
					<Text size="paragraph-1" weight="semi-bold">
						{pageHeader}
					</Text>
					<Text size="paragraph-3" weight="regular">
						Automation Assignment Log
					</Text>
					<div className={css.searchContainer}>
						<EmployeeSearch
							value={searchSubject || ""}
							onChange={(e) => handleEmployeeSearch(e as SingleEmployee)}
							placeholder="Search subject..."
							queryKey="subjectPickerSearch"
							cookieName="subjectSearchHistory"
						/>
					</div>
				</div>
			</div>

			<div className={css.tableContainer}>
				<TableLite
					columns={columns}
					rows={reviewCycleLogs}
					rowKey={"_id"}
					theme={"vJul23"}
					isLoading={isReviewCycleLogsLoading}
					headCellClass={css.headCell}
					tableBodyClass={css.tableBody}
					bodyCellClass={css.bodyCell}
					tableRowClass={css.row}
					onColumnClick={handleSortColumn}
					activeSort={currentSort.name}
					activeSortDirection={currentSort.direction}
				/>

				<ContentModal isOpen={modalContent !== null} handleClose={() => setModalContent(null)}>
					{modalContent !== null ? getModalContent(modalContent, () => setModalContent(null)) : <></>}
				</ContentModal>
			</div>

			<div className={css.paginationContainer}>
				<Pagination
					currentPage={currentPage}
					pageCount={Math.ceil(totalReviewCycleLogs / pageSize)}
					onClick={handleChangePage}
				/>
			</div>

			<div className={css.footer} />
		</div>
	);

	if (!isReviewCyclePermissionsLoading && !reviewCyclePermissions?.includes("review_cycle:viewCycleAssignmentLog")) {
		return <ErrorPage code={403} />;
	}

	return (
		<InfoPageTemplate
			pageTitle="Review Cycle Assignment Log"
			mainContent={isReviewCycleLogsLoading || isReviewCycleLoading ? <Spinner /> : mainContent}
		/>
	);
};

export default AssignmentLog;
